import { Box } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import React, { useCallback, useMemo } from "react";
import { FAMILY_HISTORY_FIELDS } from "types/family-history";

import { patientsFamilyHistoryApi } from "../../../apis";
import { FamilyHistoryResponseDto } from "../../../libs/cellera-sdk";
import { PATIENT_SECTIONS } from "./detail/PatientDetail";
import IntakeField from "./IntakeField";
import IntakeHeader from "./IntakeHeader";
import LayoutWrapper from "./LayoutWrapper";

interface FamilyHistoryProps {
  patientId: string;
}

const FamilyHistory: React.FC<FamilyHistoryProps> = ({ patientId }) => {
  const { data, isError, error } = useQuery<
    FamilyHistoryResponseDto | null,
    Error,
    FamilyHistoryResponseDto | null,
    [string, string]
  >({
    queryKey: ["familyHistory", patientId] as const,
    queryFn: async () => {
      if (!patientId) return null;
      return patientsFamilyHistoryApi.getFamilyHistoryByPatientId({
        patientId,
      });
    },
    enabled: !!patientId,
  });

  React.useEffect(() => {
    if (isError && error) {
      console.error("FamilyHistory query error:", error);
    }
  }, [isError, error]);

  const displayData = useMemo(
    () => ({
      cancer: data?.cancer ?? "",
      diabetes: data?.diabetes ?? "",
      autoimmuneDisease: data?.autoimmuneDisease ?? "",
      stroke: data?.stroke ?? "",
      other: data?.other ?? "",
      heartDisease: data?.heartDisease ?? "",
      additionalInformation: data?.additionalInformation ?? "",
    }),
    [data]
  );

  const renderField = useCallback(
    (metric: (typeof FAMILY_HISTORY_FIELDS)[number]) => {
      const value = displayData[
        metric.key as keyof typeof displayData
      ] as string;
      return <IntakeField label={metric.label} value={value} disabled={true} />;
    },
    [displayData]
  );

  return (
    <Box sx={{ mt: 2 }}>
      <IntakeHeader title={PATIENT_SECTIONS.familyHistory.label} />

      <LayoutWrapper itemsPerRow={2} spacing={3}>
        {FAMILY_HISTORY_FIELDS.map(renderField)}
      </LayoutWrapper>

      <Box sx={{ mt: 3 }}>
        <IntakeField
          label="Additional Information"
          value={displayData.additionalInformation ?? ""}
          disabled={true}
          rows={3}
        />
      </Box>
    </Box>
  );
};

export default FamilyHistory;
